import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Card, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Globe,
  FileText,
  Upload,
  Image as ImageIcon,
  Camera,
  Send,
  CheckCircle,
  Info,
} from 'lucide-react';
import { toast } from 'react-toastify';
import authService from '../services/authService';

const ID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/avif'];
const SELFIE_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/x-m4v', 'video/webm', 'video/3gpp', 'video/x-matroska'];

const AdvancedVerification = () => {
  const navigate = useNavigate();
  const [country, setCountry] = useState('');
  const [docType, setDocType] = useState('');
  const [frontIdFile, setFrontIdFile] = useState(null);
  const [backIdFile, setBackIdFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [frontIdPreview, setFrontIdPreview] = useState(null);
  const [backIdPreview, setBackIdPreview] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [kycStatus, setKycStatus] = useState(null);

  useEffect(() => {
    const fetchKYCStatus = async () => {
      try {
        const response = await authService.getKYCStatus();
        setKycStatus(response?.status || null);

        if (response?.status === 'APPROVED') {
          // Pre-fill data if available
          if (response?.country) setCountry(response.country);
          if (response?.documentType) setDocType(response.documentType);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchKYCStatus();
  }, []);

  const handleFileChange = (e, setFile, setPreview, supportedTypes, mediaName) => {
    const file = e.target.files[0];
    if (file) {
      if (!supportedTypes.includes(file.type)) {
        e.target.value = '';
        toast.error(`This ${mediaName} format is not supported. Please choose another file.`);
        return;
      }
      setFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    if (!country || !docType || !frontIdFile || !backIdFile || !selfieFile) {
      toast.error('Please complete all required fields.', {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('country', country);
      formData.append('documentType', docType);
      formData.append('idCardFront', frontIdFile);
      formData.append('idCardBack', backIdFile);
      formData.append('selfieVideo', selfieFile);

      await authService.uploadKYC(formData);

      toast.success('KYC documents submitted successfully!', {
        position: 'bottom-left',
        autoClose: 2500,
      });

      setTimeout(() => {
        navigate('/verification');
      }, 1500);
    } catch (error) {
      toast.error(error.message, {
        position: 'bottom-left',
        autoClose: 4000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '100vh',
      }}
    >
      <Container fluid className='p-0'>
        {/* Header */}
        <div className='d-flex justify-content-between align-items-center p-3 text-white'>
          <Button
            variant='link'
            onClick={() => navigate(-1)}
            className='text-white p-0'
          >
            <ArrowLeft size={24} />
          </Button>
          <h5 className='mb-0 fw-bold'>Advanced Verification</h5>
          <div style={{ width: '24px' }}></div>
        </div>

        {/* Main Content */}
        <div
          className='bg-white'
          style={{
            borderTopLeftRadius: '25px',
            borderTopRightRadius: '25px',
            minHeight: 'calc(100vh - 70px)',
          }}
        >
          <div className='p-3 p-sm-4'>
            {/* Status Banner */}
            {!loading && kycStatus === 'APPROVED' && (
              <Card
                className='border-0 shadow-sm mb-3'
                style={{ backgroundColor: '#f0fdf4' }}
              >
                <Card.Body className='p-3 d-flex align-items-start'>
                  <div
                    className='rounded-circle me-2 me-sm-3 d-flex align-items-center justify-content-center flex-shrink-0'
                    style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: '#22c55e',
                    }}
                  >
                    <CheckCircle size={20} color='white' />
                  </div>
                  <div className='flex-grow-1'>
                    <h6
                      className='fw-semibold mb-1'
                      style={{ fontSize: '0.9rem', color: '#1e293b' }}
                    >
                      ✅ KYC Approved
                    </h6>
                    <p
                      className='mb-0 text-muted'
                      style={{ fontSize: '0.8rem', lineHeight: '1.4' }}
                    >
                      Your identity verification is complete. You now have full
                      access to all features.
                    </p>
                  </div>
                </Card.Body>
              </Card>
            )}

            {!loading && kycStatus === 'PENDING' && (
              <Card
                className='border-0 shadow-sm mb-3'
                style={{ backgroundColor: '#fff7ed' }}
              >
                <Card.Body className='p-3 d-flex align-items-start'>
                  <div
                    className='rounded-circle me-2 me-sm-3 d-flex align-items-center justify-content-center flex-shrink-0'
                    style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: '#f59e0b',
                    }}
                  >
                    <Info size={20} color='white' />
                  </div>
                  <div className='flex-grow-1'>
                    <h6
                      className='fw-semibold mb-1'
                      style={{ fontSize: '0.9rem', color: '#1e293b' }}
                    >
                      ⏳ Under Review
                    </h6>
                    <p
                      className='mb-0 text-muted'
                      style={{ fontSize: '0.8rem', lineHeight: '1.4' }}
                    >
                      Your KYC documents are being reviewed. We'll notify you
                      once verification is complete.
                    </p>
                  </div>
                </Card.Body>
              </Card>
            )}

            <Card className='border-0 shadow-sm'>
              <Card.Body className='p-3 p-sm-4'>
                <h6 className='fw-bold mb-1 text-dark'>
                  {kycStatus === 'APPROVED'
                    ? 'Submitted Documents'
                    : 'Advanced KYC'}
                </h6>
                <p
                  className='text-muted small mb-4'
                  style={{ fontSize: '0.85rem' }}
                >
                  {kycStatus === 'APPROVED'
                    ? 'Your verified identity documents'
                    : 'Upload your documents for identity verification'}
                </p>

                <Form.Group className='mb-3'>
                  <Form.Label
                    className='fw-medium text-dark'
                    style={{ fontSize: '0.9rem' }}
                  >
                    <Globe
                      size={16}
                      className='me-2'
                      style={{ marginTop: '-2px' }}
                    />
                    Country
                  </Form.Label>
                  <Form.Control
                    type='text'
                    placeholder='Enter your country'
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    disabled={
                      kycStatus === 'APPROVED' || kycStatus === 'PENDING'
                    }
                    style={{
                      padding: '0.75rem',
                      border: '1.5px solid #e0e0e0',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      backgroundColor:
                        kycStatus === 'APPROVED' || kycStatus === 'PENDING'
                          ? '#f8fafc'
                          : 'white',
                    }}
                    className='shadow-none'
                  />
                </Form.Group>

                <Form.Group className='mb-3'>
                  <Form.Label
                    className='fw-medium text-dark'
                    style={{ fontSize: '0.9rem' }}
                  >
                    <FileText
                      size={16}
                      className='me-2'
                      style={{ marginTop: '-2px' }}
                    />
                    Document Type
                  </Form.Label>
                  <Form.Select
                    value={docType}
                    onChange={(e) => setDocType(e.target.value)}
                    disabled={
                      kycStatus === 'APPROVED' || kycStatus === 'PENDING'
                    }
                    style={{
                      padding: '0.75rem',
                      border: '1.5px solid #e0e0e0',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      backgroundColor:
                        kycStatus === 'APPROVED' || kycStatus === 'PENDING'
                          ? '#f8fafc'
                          : 'white',
                    }}
                    className='shadow-none'
                  >
                    <option value=''>Select document type</option>
                    <option value='passport'>Passport</option>
                    <option value='id'>National ID</option>
                    <option value='license'>Driver's License</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className='mb-3'>
                  <Form.Label
                    className='fw-medium text-dark'
                    style={{ fontSize: '0.9rem' }}
                  >
                    <ImageIcon
                      size={16}
                      className='me-2'
                      style={{ marginTop: '-2px' }}
                    />
                    Front of ID
                  </Form.Label>
                  <div
                    className='border rounded-3 p-3 text-center position-relative'
                    style={{
                      borderStyle: 'dashed',
                      borderWidth: '2px',
                      borderColor: frontIdPreview ? '#667eea' : '#e0e0e0',
                      backgroundColor: frontIdPreview ? '#f8f9ff' : '#fafafa',
                      cursor: 'pointer',
                    }}
                    onClick={() => document.getElementById('frontId').click()}
                  >
                    {frontIdPreview ? (
                      <img
                        src={frontIdPreview}
                        alt='Front ID'
                        className='img-fluid rounded'
                        style={{ maxHeight: '200px' }}
                      />
                    ) : (
                      <div className='py-3'>
                        <Upload size={32} color='#9ca3af' className='mb-2' />
                        <p
                          className='mb-0 text-muted'
                          style={{ fontSize: '0.85rem' }}
                        >
                          Click to upload front of ID
                        </p>
                      </div>
                    )}
                    <Form.Control
                      type='file'
                      accept='image/jpeg,image/png,image/webp,image/heic,image/heif,image/avif,.jpg,.jpeg,.png,.webp,.heic,.heif,.avif'
                      id='frontId'
                      onChange={(e) =>
                        handleFileChange(e, setFrontIdFile, setFrontIdPreview, ID_IMAGE_TYPES, 'image')
                      }
                      style={{ display: 'none' }}
                    />
                  </div>
                </Form.Group>

                <Form.Group className='mb-3'>
                  <Form.Label
                    className='fw-medium text-dark'
                    style={{ fontSize: '0.9rem' }}
                  >
                    <ImageIcon
                      size={16}
                      className='me-2'
                      style={{ marginTop: '-2px' }}
                    />
                    Back of ID
                  </Form.Label>
                  <div
                    className='border rounded-3 p-3 text-center position-relative'
                    style={{
                      borderStyle: 'dashed',
                      borderWidth: '2px',
                      borderColor: backIdPreview ? '#667eea' : '#e0e0e0',
                      backgroundColor: backIdPreview ? '#f8f9ff' : '#fafafa',
                      cursor: 'pointer',
                    }}
                    onClick={() => document.getElementById('backId').click()}
                  >
                    {backIdPreview ? (
                      <img
                        src={backIdPreview}
                        alt='Back ID'
                        className='img-fluid rounded'
                        style={{ maxHeight: '200px' }}
                      />
                    ) : (
                      <div className='py-3'>
                        <Upload size={32} color='#9ca3af' className='mb-2' />
                        <p
                          className='mb-0 text-muted'
                          style={{ fontSize: '0.85rem' }}
                        >
                          Click to upload back of ID
                        </p>
                      </div>
                    )}
                    <Form.Control
                      type='file'
                      accept='image/jpeg,image/png,image/webp,image/heic,image/heif,image/avif,.jpg,.jpeg,.png,.webp,.heic,.heif,.avif'
                      id='backId'
                      onChange={(e) =>
                        handleFileChange(e, setBackIdFile, setBackIdPreview, ID_IMAGE_TYPES, 'image')
                      }
                      style={{ display: 'none' }}
                    />
                  </div>
                </Form.Group>

                <Form.Group className='mb-4'>
                  <Form.Label
                    className='fw-medium text-dark'
                    style={{ fontSize: '0.9rem' }}
                  >
                    <Camera
                      size={16}
                      className='me-2'
                      style={{ marginTop: '-2px' }}
                    />
                    Video Verification
                  </Form.Label>
                  <div
                    className='border rounded-3 p-3 text-center position-relative'
                    style={{
                      borderStyle: 'dashed',
                      borderWidth: '2px',
                      borderColor: selfiePreview ? '#667eea' : '#e0e0e0',
                      backgroundColor: selfiePreview ? '#f8f9ff' : '#fafafa',
                      cursor: 'pointer',
                    }}
                    onClick={() => document.getElementById('selfie').click()}
                  >
                    {selfiePreview ? (
                      <video
                        controls
                        className='img-fluid rounded'
                        style={{ maxHeight: '200px' }}
                      >
                        <source src={selfiePreview} type='video/mp4' />
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <div className='py-3'>
                        <Camera size={32} color='#9ca3af' className='mb-2' />
                        <p
                          className='mb-0 text-muted'
                          style={{ fontSize: '0.85rem' }}
                        >
                          Click to upload verification video
                        </p>
                      </div>
                    )}
                    <Form.Control
                      type='file'
                      accept='video/mp4,video/quicktime,video/x-m4v,video/webm,video/3gpp,video/x-matroska,.mp4,.mov,.m4v,.webm,.3gp,.mkv'
                      id='selfie'
                      onChange={(e) =>
                        handleFileChange(e, setSelfieFile, setSelfiePreview, SELFIE_VIDEO_TYPES, 'video')
                      }
                      style={{ display: 'none' }}
                    />
                  </div>
                </Form.Group>

                {kycStatus === 'APPROVED' ? (
                  <Button
                    onClick={() => navigate('/verification')}
                    className='w-100 py-3 fw-semibold d-flex align-items-center justify-content-center'
                    style={{
                      background:
                        'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 4px 15px rgba(100, 116, 139, 0.4)',
                      fontSize: '0.95rem',
                    }}
                  >
                    <ArrowLeft size={18} className='me-2' />
                    Back to Verification
                  </Button>
                ) : kycStatus === 'PENDING' ? (
                  <Button
                    onClick={() => navigate('/verification')}
                    className='w-100 py-3 fw-semibold d-flex align-items-center justify-content-center'
                    style={{
                      background:
                        'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)',
                      fontSize: '0.95rem',
                    }}
                  >
                    <Info size={18} className='me-2' />
                    Under Review - Back to Verification
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className='w-100 py-3 fw-semibold d-flex align-items-center justify-content-center'
                    style={{
                      background:
                        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                      fontSize: '0.95rem',
                      opacity: submitting ? 0.7 : 1,
                    }}
                  >
                    {submitting ? (
                      <>
                        <Spinner
                          animation='border'
                          size='sm'
                          className='me-2'
                          role='status'
                        />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={18} className='me-2' />
                        Submit for Review
                      </>
                    )}
                  </Button>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>
      </Container>

      <style jsx>{`
        .form-control:focus,
        .form-select:focus {
          border-color: #667eea !important;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.15) !important;
        }

        @media (max-width: 576px) {
          h5 {
            font-size: 1.1rem !important;
          }
          h6 {
            font-size: 1rem !important;
          }
        }

        @media (max-width: 380px) {
          h5 {
            font-size: 1rem !important;
          }
          h6 {
            font-size: 0.95rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AdvancedVerification;
